<template>
  <fieldset :id="'step' + formStore.currentStep + 'Fieldset'" class="u-spaceItems3">
    <CardContent>
      <legend>
        <h2 class="u-textItalic u-textGrow2 Groove u-padBottom04">
          <span>Practice Information</span>
        </h2>
      </legend>
      <div>
        <ul class="u-listReset u-spaceItems3">
          <li
            v-for="(practice, index) in formStore.membershipApplicationInfo.practices"
            :key="practice.practiceId || index"
            class="practice-item"
          >
            <div class="practice-input-container">
              <!-- Hidden input for practice ID -->
              <input
                type="hidden"
                :id="'practiceId' + index"
                :name="'practiceId' + index"
                :value="practice.practiceId"
              />
              <label :for="'practiceName' + index" class="u-block u-textBlackAlt u-spaceBottom04">
                Practice Name {{ index + 1 }}
                <span v-if="practice.isFromTinSearch" class="u-textShrink1 u-textGray"
                  >(from API)</span
                >
                <span v-else
                  ><span class="u-hiddenVisually">(required)</span
                  ><sup class="u-textRed">*</sup></span
                >
              </label>
              <div class="InputGroup js-InputGroup">
                <input
                  v-model="practice.practiceName"
                  :id="'practiceName' + index"
                  :name="'practiceName' + index"
                  :class="`Input InputGroup-cell is-required ${getFieldError(index) ? 'u-borderColorRed' : ''}`"
                  placeholder="Enter practice name"
                  type="text"
                  required
                  :minlength="2"
                  :maxlength="100"
                  :disabled="practice.isFromTinSearch"
                  @input="handleInput(index, $event)"
                  @blur="handleBlur(index, $event)"
                />
                <button
                  v-if="
                    formStore.membershipApplicationInfo.practices.length > 1 &&
                    !practice.isFromTinSearch
                  "
                  @click="removePractice(index)"
                  type="button"
                  class="InputGroup-cell Button Button--subtle"
                  :aria-label="`Remove practice ${index + 1}`"
                >
                  <span class="u-hiddenVisually">Remove Practice</span>
                  <span class="u-textRed">
                    <svg
                      class="Icon Icon--block u-flexExpandSides"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M18.01,20.132,3.868,5.99A1.5,1.5,0,0,1,5.99,3.868L20.132,18.01a1.5,1.5,0,1,1-2.121,2.121Z"
                      />
                      <path
                        d="M3.868,20.132a1.5,1.5,0,0,1,0-2.121L18.01,3.868A1.5,1.5,0,1,1,20.132,5.99L5.99,20.132A1.5,1.5,0,0,1,3.868,20.132Z"
                      />
                    </svg>
                  </span>
                </button>
              </div>
              <!-- Validation error message -->
              <p
                class="u-textShrink1 u-textRed u-spaceTop02"
                v-if="getFieldError(index)"
                role="alert"
              >
                {{ getFieldError(index) }}
              </p>
            </div>
          </li>
        </ul>
        <div class="u-spaceTop2">
          <button
            @click="addPractice"
            type="button"
            class="Button Button--compact Button--tertiary"
          >
            <span
              ><svg
                class="Icon u-textColorInherit u-textShrink2"
                role="presentation"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  d="M22,13.5H2a1.5,1.5,0,0,1,0-3H22a1.5,1.5,0,0,1,0,3Z"
                  class="Icon-rotateCounter1of8"
                />
                <path
                  d="M12,23.5A1.50007,1.50007,0,0,1,10.5,22V2a1.5,1.5,0,0,1,3,0V22A1.50039,1.50039,0,0,1,12,23.5Z"
                  class="Icon-rotateCounter1of8"
                /></svg
            ></span>
            Add Practice
          </button>
        </div>
      </div>
    </CardContent>
  </fieldset>
</template>

<script setup>
import { computed, watch, ref, onMounted } from 'vue'
import { useFormStore } from '../../stores/formStore'
import CardContent from '../CardContent.vue'

const formStore = useFormStore()
const emit = defineEmits(['validity-changed'])

// Simple field errors tracking
const fieldErrors = ref({})

// Ensure practices exist
onMounted(() => {
  if (formStore.membershipApplicationInfo.practices.length === 0) {
    formStore.addPractice()
  }
})

// Validate a practice name
const validatePracticeName = (name) => {
  if (!name || name.trim().length < 2) {
    return 'Practice name must be at least 2 characters'
  }
  if (name.length > 100) {
    return 'Practice name must be less than 100 characters'
  }
  return null
}

// Helper function to get validation error for a specific practice
const getFieldError = (index) => {
  return fieldErrors.value[index] || null
}

// Handle input changes with validation
const handleInput = (index, event) => {
  const newValue = event.target.value

  // Update the store value
  if (formStore.membershipApplicationInfo.practices[index]) {
    formStore.membershipApplicationInfo.practices[index].practiceName = newValue
  }

  // Validate and set error
  const error = validatePracticeName(newValue)
  if (error) {
    fieldErrors.value[index] = error
  } else {
    delete fieldErrors.value[index]
  }
}

// Handle blur events with validation
const handleBlur = (index, event) => {
  const newValue = event.target.value
  const error = validatePracticeName(newValue)
  if (error) {
    fieldErrors.value[index] = error
  } else {
    delete fieldErrors.value[index]
  }
}

// Add a new practice
const addPractice = () => {
  formStore.addPractice()
}

// Remove a practice
const removePractice = (index) => {
  if (formStore.membershipApplicationInfo.practices.length > 1) {
    const practice = formStore.membershipApplicationInfo.practices[index]
    if (practice && practice.practiceId) {
      formStore.removePractice(practice.practiceId)
      delete fieldErrors.value[index]
    }
  }
}

// Step validation. TIN-search practices are read-only, so a bad name from the API
// (e.g. a blank PracticeName) can't be fixed by the user and mustn't block Next.
const isStep3Valid = computed(() => {
  return formStore.membershipApplicationInfo.practices.every((practice) => {
    if (practice.isFromTinSearch) return true
    const error = validatePracticeName(practice.practiceName)
    return !error
  })
})

// Watch for validation changes and emit to parent
watch(
  isStep3Valid,
  (newValid) => {
    formStore.setStepValidation(3, newValid)
    emit('validity-changed', newValid)
  },
  { immediate: true },
)
</script>
